import { apiClient } from '@/lib/axios';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ILeaveType {
  id: string;
  code: string;
  name: string;
  colorHex?: string | null;
  isPaid: boolean;
  requiresAttachment: boolean;
  isDisabled: boolean;
  orderNo: number;
  logCreatedAt: string;
  logUpdatedAt: string;
}

export interface ILeaveRecord {
  id: string;
  staffId: string;
  leaveTypeId: string;
  fromDate: string;
  toDate: string;
  halfDay?: 'AM' | 'PM' | null;
  returningDate: string;
  reason?: string | null;
  attachmentUrl?: string | null;
  note?: string | null;
  isDeleted: boolean;
  logCreatedAt: string;
  staff: {
    id: string;
    fullName: string;
    employeeCode: string;
    isDeleted?: boolean;
  };
  leaveType: {
    id: string;
    code: string;
    name: string;
    colorHex?: string | null;
    isPaid: boolean;
  };
}

export interface IPublicHoliday {
  id: string;
  date: string;
  name: string;
  countryCode: string;
  officeId?: string | null;
  isHalfDay: boolean;
  office?: { id: string; name: string } | null;
  logCreatedAt: string;
  logUpdatedAt: string;
}

export interface ILeaveListResponse {
  data: ILeaveRecord[];
  total: number;
  page: number;
  pageSize: number;
}

export interface IActiveLeaveCheck {
  isOnLeave: boolean;
  leaveTypeName?: string;
  leaveTypeCode?: string;
  fromDate?: string;
  toDate?: string;
  returningDate?: string;
  halfDay?: 'AM' | 'PM' | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

async function wrap<T>(promise: Promise<{ data: { success: boolean; data: T } }>): Promise<T> {
  const res = await promise;
  return res.data.data;
}

// ── LeaveType API ─────────────────────────────────────────────────────────────

export async function getLeaveTypes(): Promise<ILeaveType[]> {
  const res = await apiClient.get<{ success: boolean; data: ILeaveType[] }>('/leave-types');
  return res.data.data;
}

export async function getLeaveType(id: string): Promise<ILeaveType> {
  return wrap(apiClient.get(`/leave-types/${id}`));
}

export async function createLeaveType(dto: {
  code: string;
  name: string;
  colorHex?: string;
  isPaid: boolean;
  requiresAttachment: boolean;
  note?: string;
}): Promise<ILeaveType> {
  return wrap(apiClient.post('/leave-types', dto));
}

export async function updateLeaveType(id: string, dto: {
  name?: string;
  colorHex?: string;
  isPaid?: boolean;
  requiresAttachment?: boolean;
  note?: string;
}): Promise<ILeaveType> {
  return wrap(apiClient.put(`/leave-types/${id}`, dto));
}

export async function deleteLeaveType(id: string): Promise<void> {
  await apiClient.delete(`/leave-types/${id}`);
}

export async function toggleLeaveTypeDisabled(id: string): Promise<ILeaveType> {
  return wrap(apiClient.put(`/leave-types/${id}/toggle-disabled`));
}

// ── LeaveRecord API ───────────────────────────────────────────────────────────

export async function getLeaves(params?: {
  staffIds?: string;
  leaveTypeIds?: string;
  fromDate?: string;
  toDate?: string;
  isDeleted?: string;
  page?: string;
  pageSize?: string;
  sortBy?: string;
  sortOrder?: string;
}): Promise<ILeaveListResponse> {
  const res = await apiClient.get<{ success: boolean; data: ILeaveListResponse }>('/leaves', { params });
  return res.data.data;
}

export async function getLeave(id: string): Promise<ILeaveRecord> {
  return wrap(apiClient.get(`/leaves/${id}`));
}

export async function getStaffLeaves(staffId: string): Promise<ILeaveRecord[]> {
  const res = await apiClient.get<{ success: boolean; data: ILeaveRecord[] }>(`/leaves/staff/${staffId}`);
  return res.data.data;
}

export async function createLeave(dto: {
  staffId: string;
  leaveTypeId: string;
  fromDate: string;
  toDate: string;
  halfDay?: 'AM' | 'PM' | null;
  returningDate: string;
  reason?: string;
  attachmentUrl?: string;
  note?: string;
  confirmOverlap?: boolean;
}): Promise<ILeaveRecord> {
  return wrap(apiClient.post('/leaves', dto));
}

export async function updateLeave(id: string, dto: {
  leaveTypeId?: string;
  fromDate?: string;
  toDate?: string;
  halfDay?: 'AM' | 'PM' | null;
  returningDate?: string;
  reason?: string;
  attachmentUrl?: string;
  note?: string;
  confirmOverlap?: boolean;
}): Promise<ILeaveRecord> {
  return wrap(apiClient.put(`/leaves/${id}`, dto));
}

export async function deleteLeave(id: string): Promise<void> {
  await apiClient.delete(`/leaves/${id}`);
}

export async function suggestReturningDate(params: {
  toDate: string;
  staffId: string;
}): Promise<{ returningDate: string }> {
  return wrap(apiClient.get('/leaves/returning-date', { params }));
}

export async function checkActiveLeave(staffId: string): Promise<IActiveLeaveCheck> {
  return wrap(apiClient.get(`/leaves/check-active/${staffId}`));
}

// ── PublicHoliday API ─────────────────────────────────────────────────────────

export async function getPublicHolidays(params?: {
  year?: string;
  countryCode?: string;
  officeId?: string;
}): Promise<IPublicHoliday[]> {
  const res = await apiClient.get<{ success: boolean; data: IPublicHoliday[] }>('/public-holidays', { params });
  return res.data.data;
}

export async function getPublicHoliday(id: string): Promise<IPublicHoliday> {
  return wrap(apiClient.get(`/public-holidays/${id}`));
}

export async function createPublicHoliday(dto: {
  date: string;
  name: string;
  countryCode: string;
  officeId?: string;
  isHalfDay: boolean;
  note?: string;
}): Promise<IPublicHoliday> {
  return wrap(apiClient.post('/public-holidays', dto));
}

export async function updatePublicHoliday(id: string, dto: {
  date?: string;
  name?: string;
  countryCode?: string;
  officeId?: string | null;
  isHalfDay?: boolean;
  note?: string;
}): Promise<IPublicHoliday> {
  return wrap(apiClient.put(`/public-holidays/${id}`, dto));
}

export async function deletePublicHoliday(id: string): Promise<void> {
  await apiClient.delete(`/public-holidays/${id}`);
}
